import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { formatBedroomsLabel } from '../utils/formatBedroomsLabel';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Card image slot groups
// ============================================================================

const cardImage0Def: SlotGroupDefinition = {
  label: 'View All Card Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select the best-looking, most representative apartment interior photo from the photo gallery for this floor-plan card. Only use photos of the inside of an apartment unit — never community exterior, outdoor amenity, or indoor community amenity photos (clubhouse, fitness center, lounge, leasing office, hallways, etc.). Priority order: 1) staged living spaces, 2) staged kitchens and dining areas, 3) staged bedrooms, 4) other strong staged apartment photos, 5) unstaged living spaces, 6) unstaged kitchens and dining areas, 7) unstaged bedrooms, 8) attractive bathrooms that highlight finishes, vanities, showers, or tubs, 9) clean, visually appealing patios or balconies, 10) other useful apartment features. Always prefer staged over unstaged when a suitable staged photo is available. Prefer attractive, well-lit, clean, well-composed photos that clearly show the apartment and give a good overall impression. Avoid: bathrooms where the toilet is the primary or most prominent feature; any bathroom when a stronger living, kitchen, or bedroom photo is still unused; dirty, cluttered, or poorly maintained patios or balconies; closets or storage areas that show little; blurry, dark, low-quality, or obstructed photos; photos with excessive empty space; photos where the main subject is cut off or hard to see. When choosing between similar photos, pick the cleaner, better-composed one. Alt text: keep the existing alt text of the photo if it accurately describes it, otherwise write a short literal description of what the photo shows.',
    context:
      'Tall portrait card thumb (3:4, cropped to fill, under a dark color overlay) in the FloorplansList grid. Card 1 of 5 — the "View All Layouts" card, representing every floor plan, so use the single strongest overall apartment photo. Cards 2–5 are the bedroom-category cards. Every card must use a different photo and a different view: never pick a photo (or an essentially identical shot of the same room) already used by cards 2–5. Fall back to unstaged photos before reusing anything; reuse a photo only as a last resort when no unused eligible apartment photo remains.',
  },
};

const cardImage1Def: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select the best-looking, most representative apartment interior photo from the photo gallery for this floor-plan card. Only use photos of the inside of an apartment unit — never community exterior, outdoor amenity, or indoor community amenity photos (clubhouse, fitness center, lounge, leasing office, hallways, etc.). Priority order: 1) staged living spaces, 2) staged kitchens and dining areas, 3) staged bedrooms, 4) other strong staged apartment photos, 5) unstaged living spaces, 6) unstaged kitchens and dining areas, 7) unstaged bedrooms, 8) attractive bathrooms that highlight finishes, vanities, showers, or tubs, 9) clean, visually appealing patios or balconies, 10) other useful apartment features. Always prefer staged over unstaged when a suitable staged photo is available. Prefer attractive, well-lit, clean, well-composed photos that clearly show the apartment and give a good overall impression. Avoid: bathrooms where the toilet is the primary or most prominent feature; any bathroom when a stronger living, kitchen, or bedroom photo is still unused; dirty, cluttered, or poorly maintained patios or balconies; closets or storage areas that show little; blurry, dark, low-quality, or obstructed photos; photos with excessive empty space; photos where the main subject is cut off or hard to see. When choosing between similar photos, pick the cleaner, better-composed one. When a photo can be accurately identified (from its title, description, alt text, or content) as belonging to a unit with the bedroom count of this card, prefer it over a comparable unmatched photo — but never guess a match. Alt text: keep the existing alt text of the photo if it accurately describes it, otherwise write a short literal description of what the photo shows.',
    context:
      'Tall portrait card thumb (3:4, cropped to fill, under a dark color overlay) in the FloorplansList grid. Card 2 of 5 — the first bedroom-category card: the floor plans with the smallest bedroom count at this property (sorted studio, 1, 2, 3, 4 bed by minimum beds). Card 1 is "View All"; cards 3–5 are the next larger bedroom counts. Every card must use a different photo and a different view: never pick a photo (or an essentially identical shot of the same room) already used by cards 1, 3, 4, or 5. Fall back to unstaged photos before reusing anything; reuse a photo only as a last resort when no unused eligible apartment photo remains.',
  },
};

const cardImage2Def: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select the best-looking, most representative apartment interior photo from the photo gallery for this floor-plan card. Only use photos of the inside of an apartment unit — never community exterior, outdoor amenity, or indoor community amenity photos (clubhouse, fitness center, lounge, leasing office, hallways, etc.). Priority order: 1) staged living spaces, 2) staged kitchens and dining areas, 3) staged bedrooms, 4) other strong staged apartment photos, 5) unstaged living spaces, 6) unstaged kitchens and dining areas, 7) unstaged bedrooms, 8) attractive bathrooms that highlight finishes, vanities, showers, or tubs, 9) clean, visually appealing patios or balconies, 10) other useful apartment features. Always prefer staged over unstaged when a suitable staged photo is available. Prefer attractive, well-lit, clean, well-composed photos that clearly show the apartment and give a good overall impression. Avoid: bathrooms where the toilet is the primary or most prominent feature; any bathroom when a stronger living, kitchen, or bedroom photo is still unused; dirty, cluttered, or poorly maintained patios or balconies; closets or storage areas that show little; blurry, dark, low-quality, or obstructed photos; photos with excessive empty space; photos where the main subject is cut off or hard to see. When choosing between similar photos, pick the cleaner, better-composed one. When a photo can be accurately identified (from its title, description, alt text, or content) as belonging to a unit with the bedroom count of this card, prefer it over a comparable unmatched photo — but never guess a match. Alt text: keep the existing alt text of the photo if it accurately describes it, otherwise write a short literal description of what the photo shows.',
    context:
      'Tall portrait card thumb (3:4, cropped to fill, under a dark color overlay) in the FloorplansList grid. Card 3 of 5 — the second bedroom-category card: the second-smallest bedroom count among the floor plans at this property (sorted studio, 1, 2, 3, 4 bed by minimum beds). Card 1 is "View All"; card 2 is the smallest bedroom count; cards 4–5 are larger. Every card must use a different photo and a different view: never pick a photo (or an essentially identical shot of the same room) already used by cards 1, 2, 4, or 5. Fall back to unstaged photos before reusing anything; reuse a photo only as a last resort when no unused eligible apartment photo remains.',
  },
};

const cardImage3Def: SlotGroupDefinition = {
  label: 'Card 4 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select the best-looking, most representative apartment interior photo from the photo gallery for this floor-plan card. Only use photos of the inside of an apartment unit — never community exterior, outdoor amenity, or indoor community amenity photos (clubhouse, fitness center, lounge, leasing office, hallways, etc.). Priority order: 1) staged living spaces, 2) staged kitchens and dining areas, 3) staged bedrooms, 4) other strong staged apartment photos, 5) unstaged living spaces, 6) unstaged kitchens and dining areas, 7) unstaged bedrooms, 8) attractive bathrooms that highlight finishes, vanities, showers, or tubs, 9) clean, visually appealing patios or balconies, 10) other useful apartment features. Always prefer staged over unstaged when a suitable staged photo is available. Prefer attractive, well-lit, clean, well-composed photos that clearly show the apartment and give a good overall impression. Avoid: bathrooms where the toilet is the primary or most prominent feature; any bathroom when a stronger living, kitchen, or bedroom photo is still unused; dirty, cluttered, or poorly maintained patios or balconies; closets or storage areas that show little; blurry, dark, low-quality, or obstructed photos; photos with excessive empty space; photos where the main subject is cut off or hard to see. When choosing between similar photos, pick the cleaner, better-composed one. When a photo can be accurately identified (from its title, description, alt text, or content) as belonging to a unit with the bedroom count of this card, prefer it over a comparable unmatched photo — but never guess a match. Alt text: keep the existing alt text of the photo if it accurately describes it, otherwise write a short literal description of what the photo shows.',
    context:
      'Tall portrait card thumb (3:4, cropped to fill, under a dark color overlay) in the FloorplansList grid. Card 4 of 5 — the third bedroom-category card: the third-smallest bedroom count among the floor plans at this property (sorted studio, 1, 2, 3, 4 bed by minimum beds). Card 1 is "View All"; cards 2–3 are smaller bedroom counts; card 5 is larger. Every card must use a different photo and a different view: never pick a photo (or an essentially identical shot of the same room) already used by cards 1, 2, 3, or 5. Fall back to unstaged photos before reusing anything; reuse a photo only as a last resort when no unused eligible apartment photo remains.',
  },
};

const cardImage4Def: SlotGroupDefinition = {
  label: 'Card 5 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt:
      'Select the best-looking, most representative apartment interior photo from the photo gallery for this floor-plan card. Only use photos of the inside of an apartment unit — never community exterior, outdoor amenity, or indoor community amenity photos (clubhouse, fitness center, lounge, leasing office, hallways, etc.). Priority order: 1) staged living spaces, 2) staged kitchens and dining areas, 3) staged bedrooms, 4) other strong staged apartment photos, 5) unstaged living spaces, 6) unstaged kitchens and dining areas, 7) unstaged bedrooms, 8) attractive bathrooms that highlight finishes, vanities, showers, or tubs, 9) clean, visually appealing patios or balconies, 10) other useful apartment features. Always prefer staged over unstaged when a suitable staged photo is available. Prefer attractive, well-lit, clean, well-composed photos that clearly show the apartment and give a good overall impression. Avoid: bathrooms where the toilet is the primary or most prominent feature; any bathroom when a stronger living, kitchen, or bedroom photo is still unused; dirty, cluttered, or poorly maintained patios or balconies; closets or storage areas that show little; blurry, dark, low-quality, or obstructed photos; photos with excessive empty space; photos where the main subject is cut off or hard to see. When choosing between similar photos, pick the cleaner, better-composed one. When a photo can be accurately identified (from its title, description, alt text, or content) as belonging to a unit with the bedroom count of this card, prefer it over a comparable unmatched photo — but never guess a match. Alt text: keep the existing alt text of the photo if it accurately describes it, otherwise write a short literal description of what the photo shows.',
    context:
      'Tall portrait card thumb (3:4, cropped to fill, under a dark color overlay) in the FloorplansList grid. Card 5 of 5 — the fourth bedroom-category card: the fourth-smallest bedroom count among the floor plans at this property (sorted studio, 1, 2, 3, 4 bed by minimum beds). Card 1 is "View All"; cards 2–4 are smaller bedroom counts. Every card must use a different photo and a different view: never pick a photo (or an essentially identical shot of the same room) already used by cards 1–4. Fall back to unstaged photos before reusing anything; reuse a photo only as a last resort when no unused eligible apartment photo remains.',
  },
};

// ============================================================================
// Category card sub-component (renders card image + overlay + label)
// ============================================================================

interface CategoryCardProps {
  cat: BedroomCategory;
  children: React.ReactNode; // The <EditableSlotGroup> wrapper with literal groupPath
}

function CategoryCard({ cat, children }: CategoryCardProps) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card group relative block aspect-[3/4] overflow-hidden cursor-pointer fp-card-item"
      style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
    >
      {children}
      <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
        <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
          {cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString()}
        </span>
        <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
          {cat.label} {cat.key === 'studio' ? '' : 'Layouts'}
        </span>
      </div>
    </a>
  );
}

function CardImageContent({ img, fallbackAlt }: { img: { image?: string; alt?: string }; fallbackAlt: string }) {
  return img.image ? (
    <VibesitesImage
      url={img.image}
      alt={img.alt || fallbackAlt}
      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
      sizes="(min-width: 768px) 280px, 50vw"
    />
  ) : (
    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const label = useContentToken('floorplanslist.label');
  const title = useContentToken('floorplanslist.title');
  const subtitle = useContentToken('floorplanslist.subtitle');

  // Expand dash ranges (e.g. "1 - 3") into listed options at render time so
  // field-mapped / overridden values still update (not only schema defaults).
  const formattedLabel = useMemo(() => {
    if (!label) return null;
    const beds = formatBedroomsLabel(label);
    const base = beds.replace(/\s+Bedrooms?$/i, '');
    return `${base} Bedroom Layouts`;
  }, [label]);

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  const img0 = useSlotGroup('floorplanslist.card_image_0', cardImage0Def);
  const img1 = useSlotGroup('floorplanslist.card_image_1', cardImage1Def);
  const img2 = useSlotGroup('floorplanslist.card_image_2', cardImage2Def);
  const img3 = useSlotGroup('floorplanslist.card_image_3', cardImage3Def);
  const img4 = useSlotGroup('floorplanslist.card_image_4', cardImage4Def);

  // Category cards with their image slots (indices 1-4)
  const catSlotImages = [img1, img2, img3, img4];
  const catSlotDefs = [cardImage1Def, cardImage2Def, cardImage3Def, cardImage4Def];

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <div className="max-w-[1400px] mx-auto px-2 sm:px-3 lg:px-4">
        <div ref={headingRef} className="reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <EditableToken path="floorplanslist.title" defaultValue="Make Yourself at Home" as="h2" className="font-heading text-3xl sm:text-5xl font-bold text-[var(--vibe-color-text-on-primary)] mb-4 tracking-tight">
            {title}
          </EditableToken>
          <EditableToken path="floorplanslist.subtitle" defaultValue="Please select your desired layout below." as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto">
            {subtitle}
          </EditableToken>
        </div>

        {hasCategories ? (
          <div ref={gridRef} className="fp-grid flex flex-wrap md:flex-nowrap gap-3 md:gap-4">
            {/* "View All" card first — uses slot index 0 */}
            <a
              data-reveal
              href="/floor-plans"
              className="reveal-fade-up fp-card group relative block aspect-[3/4] overflow-hidden cursor-pointer fp-card-item"
              style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
            >
              <EditableSlotGroup groupPath="floorplanslist.card_image_0" definition={cardImage0Def}>
                {img0.image ? (
                  <VibesitesImage
                    url={img0.image}
                    alt={img0.alt || 'View all floor plans'}
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                    sizes="(min-width: 768px) 280px, 50vw"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>
              <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
                <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">ALL</span>
                <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">View All Layouts</span>
              </div>
            </a>

            {/* Bedroom category cards — each uses a literal groupPath for static linter detection */}
            {categories.length > 0 && (
              <CategoryCard cat={categories[0]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_1" definition={catSlotDefs[0]}>
                  <CardImageContent img={catSlotImages[0]} fallbackAlt={`${categories[0].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 1 && (
              <CategoryCard cat={categories[1]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_2" definition={catSlotDefs[1]}>
                  <CardImageContent img={catSlotImages[1]} fallbackAlt={`${categories[1].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 2 && (
              <CategoryCard cat={categories[2]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_3" definition={catSlotDefs[2]}>
                  <CardImageContent img={catSlotImages[2]} fallbackAlt={`${categories[2].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 3 && (
              <CategoryCard cat={categories[3]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_4" definition={catSlotDefs[3]}>
                  <CardImageContent img={catSlotImages[3]} fallbackAlt={`${categories[3].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-primary) 70%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 100%
          );
          opacity: 0.8;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.88;
        }
        /* On mobile only, cards take ~half width so they wrap in pairs.
           Tablets (≥768px) use the single-row desktop layout. */
        @media (max-width: 767px) {
          .fp-card-item {
            flex: 0 0 calc(50% - 0.375rem) !important;
            min-width: calc(50% - 0.375rem) !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing bedroom options (e.g., "1, 2, and 3 Bedroom Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Make Yourself at Home',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'text',
      defaultValue: 'Please select your desired layout below.',
    },
  },
  slotGroups: {
    'floorplanslist.card_image_0': cardImage0Def,
    'floorplanslist.card_image_1': cardImage1Def,
    'floorplanslist.card_image_2': cardImage2Def,
    'floorplanslist.card_image_3': cardImage3Def,
    'floorplanslist.card_image_4': cardImage4Def,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
